import { DecimalPipe } from '@angular/common';
import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';

import { AnalyticsDonutComponent } from '../../../shared/components/analytics/analytics-donut/analytics-donut.component';
import { AnalyticsSeriesChartComponent } from '../../../shared/components/analytics/analytics-series-chart/analytics-series-chart.component';
import {
  PrettySelectComponent,
  PrettySelectOption,
} from '../../../shared/components/ui/pretty-select/pretty-select.component';
import { toErrorMessage } from '../../../shared/helpers/http-error';
import {
  AnalyticsService,
  isUnresolvedScope,
} from '../../../shared/services/analytics.service';
import { LocaleService } from '../../../shared/services/locale.service';
import {
  PeriodPreset,
  SeriesPoint,
  SettlementSummary,
  resolvePeriod,
} from '../../../shared/types/analytics.types';
import { TPipe } from '../../../shared/pipe/t.pipe';

/**
 * Section analytics du dashboard **operator** (slice 0 : **règlements seuls** —
 * ni paiements PLATFORM-only, ni notes non ventilables par opérateur).
 *
 * Auto-scopée (JWT). `grossAmount` = volume intermédié, `operatorAmount` = la
 * commission perçue par l'opérateur ; `platformAmount` reste la part plateforme
 * du même règlement — colonnes d'un **même** périmètre, jamais un autre scope.
 * 403 « acteur non résolvable » → message dédié.
 */
@Component({
  selector: 'app-operator-analytics',
  standalone: true,
  imports: [
    DecimalPipe,
    FormsModule,
    TPipe,
    PrettySelectComponent,
    AnalyticsSeriesChartComponent,
    AnalyticsDonutComponent,
  ],
  templateUrl: './operator-analytics.component.html',
})
export class OperatorAnalyticsComponent implements OnInit {
  private readonly analytics = inject(AnalyticsService);
  private readonly locale = inject(LocaleService);
  private readonly destroyRef = inject(DestroyRef);

  readonly period = signal<PeriodPreset>('30d');
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly unresolved = signal(false);

  readonly settlement = signal<SettlementSummary | null>(null);
  readonly series = signal<SeriesPoint[]>([]);

  readonly periodOptions: PrettySelectOption<PeriodPreset>[] = [
    { value: '7d', label: 'analytics.period.7d' },
    { value: '30d', label: 'analytics.period.30d' },
    { value: '90d', label: 'analytics.period.90d' },
    { value: '365d', label: 'analytics.period.365d' },
  ];

  ngOnInit(): void {
    this.load();
  }

  changePeriod(p: PeriodPreset | null): void {
    if (!p || p === this.period()) return;
    this.period.set(p);
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.unresolved.set(false);
    const period = resolvePeriod(this.period());

    forkJoin({
      settlement: this.analytics.settlementSummary(period),
      series: this.analytics.settlementSeries(period),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ settlement, series }) => {
          this.settlement.set(settlement);
          this.series.set(series);
          this.loading.set(false);
        },
        error: (err: unknown) => {
          this.loading.set(false);
          if (isUnresolvedScope(err)) {
            this.unresolved.set(true);
          } else {
            this.error.set(toErrorMessage(err, 'errors.analyticsLoadFailed'));
          }
        },
      });
  }

  seriesName(): string {
    return this.locale.t('analytics.chart.volume');
  }
}
